import React, { useState, useRef, useEffect } from 'react';
import { Filter, Search, Check, BarChart2, Hash } from 'lucide-react';
import { OrderStatus } from '@onesunday/domain';
import { CountMode } from '../types';
import { MENU_ITEMS, STATUS_STYLES } from '../constants';

interface FilterBarProps {
  selectedItems: string[];
  onToggleItem: (itemId: string) => void;
  showCompleted: boolean;
  onToggleShowCompleted: () => void;
  showReady: boolean;
  onToggleShowReady: () => void;
  statusFilters: (OrderStatus | 'Late')[];
  onToggleStatusFilter: (status: OrderStatus | 'Late') => void;
  onResetStatusFilters: () => void;
  hourFilters: string[];
  onToggleHourFilter: (hour: string) => void;
  onResetHourFilters: () => void;
  hourOptions: string[];
  countMode: CountMode;
  onToggleCountMode: (mode: CountMode) => void;
  onClearItems: () => void;
  className?: string;
  statusCounts: Record<OrderStatus, number>;
  late: number;
  revenueText: string;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  selectedItems,
  onToggleItem,
  showCompleted,
  onToggleShowCompleted,
  showReady,
  onToggleShowReady,
  statusFilters,
  onToggleStatusFilter,
  onResetStatusFilters,
  hourFilters,
  onToggleHourFilter,
  onResetHourFilters,
  hourOptions,
  countMode,
  onToggleCountMode,
  onClearItems,
  className,
  statusCounts,
  late,
  revenueText,
}) => {
  const [isItemDropdownOpen, setIsItemDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isHourDropdownOpen, setIsHourDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const itemDropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const hourDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (itemDropdownRef.current && !itemDropdownRef.current.contains(target)) {
        setIsItemDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(target)) {
        setIsStatusDropdownOpen(false);
      }
      if (hourDropdownRef.current && !hourDropdownRef.current.contains(target)) {
        setIsHourDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredMenuItems = MENU_ITEMS.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const activeItemNames = MENU_ITEMS.filter((i) => selectedItems.includes(i.id)).map(
    (i) => i.name,
  );

  const containerClasses =
    className ||
    'flex flex-col md:flex-row md:items-center justify-between gap-4 px-4 py-3';

  const statusOptions: (OrderStatus | 'Late')[] = ['Paid', 'Ready', 'Completed', 'Late'];
  const availableHourOptions = hourOptions;

  return (
    <div className={containerClasses}>
      <div className="flex flex-wrap gap-2 items-center">
        {(['Paid', 'Ready', 'Completed'] as OrderStatus[]).map((status) => {
          const styles = STATUS_STYLES[status];
          return (
          <div
            key={status}
            className={`px-3 py-2 rounded-full border flex items-center gap-2 shadow-sm text-sm ${styles.bg} ${styles.text} ${styles.border}`}
          >
            <span className="text-[11px] uppercase tracking-wide text-admin-muted">{status}</span>
            <span className="text-base font-semibold text-admin-text">{statusCounts[status] || 0}</span>
          </div>
        )})}
        {(() => {
          const styles = STATUS_STYLES.Late;
          return (
            <div className={`px-3 py-2 rounded-full border flex items-center gap-2 shadow-sm text-sm ${styles.bg} ${styles.text} ${styles.border}`}>
              <span className="text-[11px] uppercase tracking-wide">{'Late'}</span>
              <span className="text-base font-semibold">{late}</span>
            </div>
          );
        })()}
      </div>

      <div className="flex items-center gap-3 flex-1">
        <div className="relative" ref={itemDropdownRef}>
          <button
            onClick={() => setIsItemDropdownOpen(!isItemDropdownOpen)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
              selectedItems.length > 0
                ? 'bg-admin-accent/18 border-admin-accent/40 text-admin-text'
                : 'bg-admin-surface border-admin-border text-admin-text hover:bg-admin-accent-soft'
            }`}
          >
            <Filter size={18} />
            <span className="font-medium">Filter Items</span>
            {selectedItems.length > 0 && (
              <span className="bg-flyer-red text-white text-xs rounded-full w-5 h-5 flex items-center justify-center ml-1">
                {selectedItems.length}
              </span>
            )}
          </button>

          {isItemDropdownOpen && (
            <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-admin-border p-2 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top-left z-20">
              <div className="relative mb-2 px-1">
                <Search className="absolute left-3 top-2.5 text-admin-subtle" size={16} />
                <input
                  type="text"
                  placeholder="Search menu..."
                  className="w-full pl-9 pr-3 py-2 bg-admin-surface-2 rounded-lg text-sm outline-none border border-transparent focus:border-admin-accent focus:bg-white transition-all"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="max-h-60 overflow-y-auto">
                {filteredMenuItems.length === 0 ? (
                  <div className="text-center py-4 text-admin-subtle text-sm">No items found</div>
                ) : (
                  filteredMenuItems.map((item) => {
                    const isSelected = selectedItems.includes(item.id);
                    return (
                      <div
                        key={item.id}
                        onClick={() => onToggleItem(item.id)}
                        className={`flex items-center justify-between px-3 py-2 rounded-md cursor-pointer text-sm mb-1 transition-colors ${
                          isSelected ? 'bg-admin-accent/18 text-admin-text' : 'hover:bg-admin-accent-soft text-admin-text/80'
                        }`}
                      >
                        <span>{item.name}</span>
                        {isSelected && <Check size={16} className="text-flyer-red" />}
                      </div>
                    );
                  })
                )}
              </div>

              {selectedItems.length > 0 && (
                <div className="mt-2 pt-2 border-t border-admin-border">
                  <button
                    onClick={onClearItems}
                    className="w-full text-center text-xs text-red-500 hover:bg-red-50 py-1.5 rounded-md font-medium transition-colors"
                  >
                    Clear Selection
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="hidden md:flex flex-wrap gap-2 items-center overflow-hidden h-9">
          {activeItemNames.slice(0, 3).map((name, idx) => (
            <span
              key={idx}
              className="bg-admin-accent/18 text-admin-text text-xs px-2 py-1 rounded-full border border-admin-accent/35 flex items-center gap-1"
            >
              {name}
            </span>
          ))}
          {activeItemNames.length > 3 && (
            <span className="text-xs text-admin-muted font-medium">
              +{activeItemNames.length - 3} more
            </span>
          )}
        </div>
      </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative" ref={hourDropdownRef}>
            <button
              onClick={() => setIsHourDropdownOpen(!isHourDropdownOpen)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                hourFilters.length > 0
                  ? 'bg-flyer-yellow/20 border-flyer-yellow/40 text-admin-text'
                  : 'bg-admin-surface border-admin-border text-admin-text hover:bg-admin-accent-soft'
              }`}
            >
              <Filter size={18} />
              <span className="font-medium">Hours</span>
              <span className="bg-admin-surface-2 text-admin-muted text-xs rounded-full px-2 py-0.5">
                {hourFilters.length || 'All'}
              </span>
            </button>

            {isHourDropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-admin-border p-2 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top-left z-20">
                <div className="max-h-60 overflow-y-auto">
                  {availableHourOptions.map((hour) => {
                    const isSelected = hourFilters.includes(hour);
                    return (
                      <div
                        key={hour}
                        onClick={() => onToggleHourFilter(hour)}
                        className={`flex items-center justify-between px-3 py-2 rounded-md cursor-pointer text-sm mb-1 transition-colors ${
                          isSelected ? 'bg-flyer-yellow/20 text-admin-text' : 'hover:bg-admin-accent-soft text-admin-text/80'
                        }`}
                      >
                        <span>{hour}</span>
                        {isSelected && <Check size={16} className="text-flyer-red" />}
                      </div>
                    );
                  })}
                </div>
                {availableHourOptions.length === 0 && (
                  <div className="text-center py-4 text-admin-subtle text-sm">No campaign hours in range</div>
                )}
                <div className="mt-2 pt-2 border-t border-admin-border flex gap-2">
                  <button
                    onClick={onResetHourFilters}
                    className="flex-1 text-center text-xs text-flyer-red hover:bg-admin-accent-soft py-1.5 rounded-md font-medium transition-colors"
                  >
                    All Hours
                  </button>
                  <button
                    onClick={() => {
                      hourFilters.forEach((h) => onToggleHourFilter(h));
                    }}
                    className="flex-1 text-center text-xs text-red-500 hover:bg-red-50 py-1.5 rounded-md font-medium transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="relative" ref={statusDropdownRef}>
            <button
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                statusFilters.length !== statusOptions.length
                  ? 'bg-admin-accent/18 border-admin-accent/40 text-admin-text'
                  : 'bg-admin-surface border-admin-border text-admin-text hover:bg-admin-accent-soft'
              }`}
            >
              <Filter size={18} />
              <span className="font-medium">Statuses</span>
              <span className="bg-admin-surface-2 text-admin-muted text-xs rounded-full px-2 py-0.5">
                {statusFilters.length}/{statusOptions.length}
              </span>
            </button>

            {isStatusDropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-admin-border p-2 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top-left z-20">
                <div className="max-h-60 overflow-y-auto">
                  {statusOptions.map((status) => {
                    const isSelected = statusFilters.includes(status);
                    return (
                      <div
                        key={status}
                        onClick={() => onToggleStatusFilter(status)}
                        className={`flex items-center justify-between px-3 py-2 rounded-md cursor-pointer text-sm mb-1 transition-colors ${
                          isSelected ? 'bg-admin-accent/18 text-admin-text' : 'hover:bg-admin-accent-soft text-admin-text/80'
                        }`}
                      >
                        <span>{status === 'Late' ? 'Late (Paid)' : status}</span>
                        {isSelected && <Check size={16} className="text-flyer-red" />}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 pt-2 border-t border-admin-border flex gap-2">
                  <button
                    onClick={onResetStatusFilters}
                    className="flex-1 text-center text-xs text-flyer-red hover:bg-admin-accent-soft py-1.5 rounded-md font-medium transition-colors"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => {
                      statusOptions.forEach((s) => {
                        if (statusFilters.includes(s)) onToggleStatusFilter(s);
                      });
                    }}
                    className="flex-1 text-center text-xs text-red-500 hover:bg-red-50 py-1.5 rounded-md font-medium transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex bg-admin-surface-2 p-1 rounded-lg border border-admin-border">
            <button
              onClick={() => onToggleCountMode('orders')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              countMode === 'orders' ? 'bg-white shadow-sm text-admin-text' : 'text-admin-subtle hover:text-admin-text'
            }`}
          >
            <Hash size={14} />
            Orders
          </button>
          <button
            onClick={() => onToggleCountMode('items')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              countMode === 'items' ? 'bg-white shadow-sm text-flyer-red' : 'text-admin-subtle hover:text-admin-text'
            }`}
          >
            <BarChart2 size={14} />
            Items
          </button>
      </div>

      </div>
    </div>
  );
};
